import React, { useEffect, useRef, useState } from "react";

const About = () => {
  const faceImg = require("../assets/images/face.jpeg");
  const [heroVisible, setHeroVisible] = useState(false);
  const [aboutVisible, setAboutVisible] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const makeObserver = (
      ref: React.RefObject<HTMLDivElement>,
      setter: (v: boolean) => void,
    ) => {
      const obs = new IntersectionObserver(
        ([e]) => {
          if (e.isIntersecting) setter(true);
        },
        { threshold: 0.15 },
      );
      if (ref.current) obs.observe(ref.current);
      return obs;
    };
    const o1 = makeObserver(heroRef, setHeroVisible);
    const o2 = makeObserver(aboutRef, setAboutVisible);
    return () => {
      o1.disconnect();
      o2.disconnect();
    };
  }, []);

  const socials = [
    {
      href: "https://github.com/codewithadelite",
      icon: "fa-brands fa-github",
      label: "GitHub",
    },
    {
      href: "https://linkedin.com/in/niyonshuti-shema-adelite-5b078a278",
      icon: "fa-brands fa-linkedin-in",
      label: "LinkedIn",
    },
    {
      href: "mailto:niadelite@gmail.com",
      icon: "fa-regular fa-envelope",
      label: "Email",
    },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

        /* ── Shared ── */
        .about-page {
          background: #0a0a0f;
          font-family: 'DM Sans', sans-serif;
          color: #f0f0f0;
        }

        /* ── HERO ── */
        .hero-section {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 80px 24px;
          position: relative;
          overflow: hidden;
        }

        /* Grid background */
        .hero-section::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
          background-size: 60px 60px;
          pointer-events: none;
        }

        /* Bottom orb */
        .hero-section::after {
          content: '';
          position: absolute;
          width: 700px;
          height: 700px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(74,222,128,0.06) 0%, transparent 70%);
          bottom: -200px;
          left: -100px;
          pointer-events: none;
        }

        .hero-inner {
          max-width: 1100px;
          width: 100%;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
          position: relative;
          z-index: 1;
        }

        @media (max-width: 768px) {
          .hero-inner {
            grid-template-columns: 1fr;
            gap: 48px;
            text-align: center;
          }
          .hero-socials { justify-content: center; }
          .hero-img-col { order: -1; }
        }

        /* Text side */
        .hero-text-col { }

        .hero-eyebrow {
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #4ade80;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s;
        }
        .hero-eyebrow.visible { opacity: 1; transform: translateY(0); }
        .hero-eyebrow::after {
          content: '';
          display: block;
          width: 40px;
          height: 1px;
          background: #4ade80;
          opacity: 0.6;
        }
        @media (max-width: 768px) {
          .hero-eyebrow { justify-content: center; }
          .hero-eyebrow::after { display: none; }
        }

        .hero-name {
          font-family: 'Syne', sans-serif;
          font-size: clamp(36px, 5.5vw, 68px);
          font-weight: 800;
          line-height: 1.05;
          letter-spacing: -0.03em;
          margin: 0 0 8px 0;
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s;
        }
        .hero-name.visible { opacity: 1; transform: translateY(0); }

        .hero-name-accent {
          background: linear-gradient(135deg, #4ade80 0%, #22d3ee 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-fullname {
          font-family: 'Syne', sans-serif;
          font-size: clamp(13px, 1.8vw, 18px);
          font-weight: 600;
          letter-spacing: 0.06em;
          color: rgba(240,240,240,0.35);
          text-transform: uppercase;
          margin: 0 0 28px 0;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease 0.3s, transform 0.6s ease 0.3s;
        }
        .hero-fullname.visible { opacity: 1; transform: translateY(0); }

        .hero-tagline {
          font-size: 16px;
          font-weight: 300;
          font-style: italic;
          color: rgba(240,240,240,0.5);
          margin: 0 0 40px 0;
          line-height: 1.6;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease 0.4s, transform 0.6s ease 0.4s;
        }
        .hero-tagline.visible { opacity: 1; transform: translateY(0); }

        .hero-socials {
          display: flex;
          gap: 12px;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease 0.5s, transform 0.6s ease 0.5s;
        }
        .hero-socials.visible { opacity: 1; transform: translateY(0); }

        .social-btn {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          border: 1px solid rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.04);
          color: rgba(240,240,240,0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          font-size: 16px;
          transition: background 0.25s, border-color 0.25s, color 0.25s, transform 0.25s;
        }
        .social-btn:hover {
          background: rgba(74,222,128,0.12);
          border-color: rgba(74,222,128,0.4);
          color: #4ade80;
          transform: translateY(-3px);
        }

        /* Image side */
        .hero-img-col {
          display: flex;
          justify-content: center;
          align-items: center;
          opacity: 0;
          transform: translateX(30px);
          transition: opacity 0.8s ease 0.35s, transform 0.8s ease 0.35s;
        }
        .hero-img-col.visible { opacity: 1; transform: translateX(0); }
        @media (max-width: 768px) {
          .hero-img-col { transform: translateY(-20px); }
          .hero-img-col.visible { transform: translateY(0); }
        }

        .hero-img-frame {
          position: relative;
          width: clamp(220px, 32vw, 360px);
          aspect-ratio: 1 / 1.15;
        }

        /* Decorative corner brackets */
        .hero-img-frame::before,
        .hero-img-frame::after {
          content: '';
          position: absolute;
          width: 32px;
          height: 32px;
          border-color: #4ade80;
          border-style: solid;
          opacity: 0.5;
          z-index: 2;
        }
        .hero-img-frame::before {
          top: -6px; left: -6px;
          border-width: 2px 0 0 2px;
        }
        .hero-img-frame::after {
          bottom: -6px; right: -6px;
          border-width: 0 2px 2px 0;
        }

        .hero-img-frame img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 20px;
          display: block;
          filter: grayscale(15%) contrast(1.05);
          border: 1px solid rgba(255,255,255,0.08);
        }

        /* Floating dot accent */
        .hero-img-dot {
          position: absolute;
          bottom: -18px;
          left: -18px;
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(74,222,128,0.25) 0%, transparent 70%);
          z-index: 0;
        }

        /* ── ABOUT SECTION ── */
        .about-section {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 80px 24px;
          position: relative;
          overflow: hidden;
        }

        .about-section::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
          background-size: 60px 60px;
          pointer-events: none;
        }

        .about-section::after {
          content: '';
          position: absolute;
          width: 500px;
          height: 500px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(34,211,238,0.05) 0%, transparent 70%);
          top: -80px;
          right: -60px;
          pointer-events: none;
        }

        .about-inner {
          max-width: 860px;
          width: 100%;
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .about-eyebrow {
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #4ade80;
          margin-bottom: 16px;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s;
        }
        .about-eyebrow.visible { opacity: 1; transform: translateY(0); }

        .about-title {
          font-family: 'Syne', sans-serif;
          font-size: clamp(36px, 5vw, 64px);
          font-weight: 800;
          letter-spacing: -0.03em;
          line-height: 1;
          margin: 0 0 48px 0;
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s;
        }
        .about-title.visible { opacity: 1; transform: translateY(0); }

        .about-title-accent {
          background: linear-gradient(135deg, #4ade80 0%, #22d3ee 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .about-divider {
          width: 100%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(74,222,128,0.4), transparent);
          margin-bottom: 48px;
          opacity: 0;
          transition: opacity 0.6s ease 0.3s;
        }
        .about-divider.visible { opacity: 1; }

        .about-bio {
          font-size: clamp(15px, 2vw, 18px);
          font-weight: 300;
          color: rgba(240,240,240,0.55);
          line-height: 1.85;
          max-width: 680px;
          margin: 0 0 56px 0;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.7s ease 0.4s, transform 0.7s ease 0.4s;
        }
        .about-bio.visible { opacity: 1; transform: translateY(0); }

        .about-bio strong {
          background: linear-gradient(135deg, #4ade80 0%, #22d3ee 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: 500;
        }

        /* Stat pills */
        .about-stats {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          justify-content: center;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease 0.55s, transform 0.6s ease 0.55s;
        }
        .about-stats.visible { opacity: 1; transform: translateY(0); }

        .stat-pill {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 100px;
          padding: 10px 24px;
          display: flex;
          align-items: center;
          gap: 10px;
          transition: background 0.25s, border-color 0.25s;
        }
        .stat-pill:hover {
          background: rgba(74,222,128,0.06);
          border-color: rgba(74,222,128,0.25);
        }
        .stat-pill-icon {
          font-size: 13px;
          color: #4ade80;
        }
        .stat-pill-text {
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: rgba(240,240,240,0.4);
        }
      `}</style>

      <div className="about-page">
        {/* ── HERO ── */}
        <section className="hero-section" ref={heroRef}>
          <div className="hero-inner">
            {/* Text */}
            <div className="hero-text-col">
              <p className={`hero-eyebrow ${heroVisible ? "visible" : ""}`}>
                Portfolio
              </p>
              <h1 className={`hero-name ${heroVisible ? "visible" : ""}`}>
                Hi, I'm
                <br />
                <span className="hero-name-accent">Adelite</span>
              </h1>
              <p className={`hero-fullname ${heroVisible ? "visible" : ""}`}>
                Niyonshuti Shema
              </p>
              <p className={`hero-tagline ${heroVisible ? "visible" : ""}`}>
                Full-Stack & LLM Developer combining modern web engineering with
                AI to build intelligent applications.
              </p>
              <div className={`hero-socials ${heroVisible ? "visible" : ""}`}>
                {socials.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    className="social-btn"
                    aria-label={s.label}
                  >
                    <i className={s.icon} />
                  </a>
                ))}
              </div>
            </div>

            {/* Image */}
            <div className={`hero-img-col ${heroVisible ? "visible" : ""}`}>
              <div className="hero-img-frame">
                <img src={faceImg} alt="Adelite Shema Niyonshuti" />
                <div className="hero-img-dot" />
              </div>
            </div>
          </div>
        </section>

        {/* ── ABOUT ── */}
        <section className="about-section" ref={aboutRef}>
          <div className="about-inner">
            <p className={`about-eyebrow ${aboutVisible ? "visible" : ""}`}>
              About Me
            </p>
            <h2 className={`about-title ${aboutVisible ? "visible" : ""}`}>
              Who I <span className="about-title-accent">Am</span>
            </h2>

            <div className={`about-divider ${aboutVisible ? "visible" : ""}`} />

            <p className={`about-bio ${aboutVisible ? "visible" : ""}`}>
              As a <strong>full-stack developer</strong> I build AI-powered
              applications end-to-end from <strong>Python</strong> backends to
              frontend interfaces with <strong>Typescript/Next.js</strong>. I've
              demonstrated the ability to work both independently with minimal
              supervision and collaboratively within a team — always focused on
              delivering results.
            </p>

            <div className={`about-stats ${aboutVisible ? "visible" : ""}`}>
              {[
                { icon: "fa-solid fa-code", label: "Full-Stack Dev" },
                {
                  icon: "fa-solid fa-layer-group",
                  label: "Python & TypeScript",
                },

                { icon: "fa-solid fa-robot", label: "AI-Powered Apps" },
                { icon: "fa-solid fa-users", label: "Team Player" },
              ].map((s) => (
                <div className="stat-pill" key={s.label}>
                  <i className={`${s.icon} stat-pill-icon`} />
                  <span className="stat-pill-text">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;
